import React, { useEffect, useState } from 'react';
import {
  deleteCompanyCustomField,
  fetchCompanyCustomFields,
  upsertCompanyCustomField,
} from '../../services/companyCustomizationApi';
import type { CompanyCustomField } from '../../services/companyCustomizationApi';
import { safeJsonParse, toPrettyJson } from '../../utils/json';
import { useI18n } from '../../contexts/I18nContext';

interface CompanyCustomFieldsManagerProps {
  companyId: string;
}

const fieldTypes = [
  'text',
  'textarea',
  'number',
  'select',
  'multiselect',
  'checkbox',
  'date',
  'time',
];

const CompanyCustomFieldsManager: React.FC<CompanyCustomFieldsManagerProps> = ({ companyId }) => {
  const { t } = useI18n();
  const [fields, setFields] = useState<CompanyCustomField[]>([]);
  const [moduleFilter, setModuleFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState({
    module: '',
    fieldKey: '',
    label: '',
    fieldType: 'text',
    required: false,
    position: 0,
    configText: '',
    validationsText: '',
    isActive: true,
  });

  const loadFields = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCompanyCustomFields(companyId, moduleFilter || undefined);
      setFields(data);
    } catch (err) {
      console.error('[CompanyCustomFieldsManager] Failed to load custom fields', err);
      setError('Não foi possível carregar campos customizados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, moduleFilter]);

  const resetFeedback = () => {
    setError(null);
    setMessage(null);
  };

  const handleEdit = (field: CompanyCustomField) => {
    resetFeedback();
    setForm({
      module: field.module,
      fieldKey: field.fieldKey,
      label: field.label,
      fieldType: field.fieldType,
      required: field.required,
      position: field.position,
      configText: toPrettyJson(field.config),
      validationsText: toPrettyJson(field.validations),
      isActive: field.isActive,
    });
  };

  const handleDelete = async (field: CompanyCustomField) => {
    if (
      !window.confirm(`Deseja realmente remover o campo "${field.label}" do módulo "${field.module}"?`)
    ) {
      return;
    }
    resetFeedback();
    setSaving(true);
    try {
      await deleteCompanyCustomField(companyId, field.module, field.fieldKey);
      await loadFields();
      setMessage('Campo removido com sucesso.');
    } catch (err) {
      console.error('[CompanyCustomFieldsManager] Failed to delete field', err);
      setError(t('customization.fields.errorRemove'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    resetFeedback();

    if (!form.module.trim() || !form.fieldKey.trim() || !form.label.trim()) {
      setError('Informe módulo, chave e label do campo.');
      return;
    }

    let config: Record<string, unknown> = {};
    let validations: Record<string, unknown> = {};

    try {
      config = form.configText.trim() ? safeJsonParse(form.configText, config) : {};
      validations = form.validationsText.trim()
        ? safeJsonParse(form.validationsText, validations)
        : {};
    } catch {
      setError('JSON de configuração ou validação inválido.');
      return;
    }

    setSaving(true);
    try {
      await upsertCompanyCustomField(companyId, {
        module: form.module.trim(),
        fieldKey: form.fieldKey.trim(),
        label: form.label.trim(),
        fieldType: form.fieldType,
        required: form.required,
        position: form.position,
        config,
        validations,
        isActive: form.isActive,
      });
      await loadFields();
      setMessage('Campo salvo com sucesso.');
    } catch (err) {
      console.error('[CompanyCustomFieldsManager] Failed to save field', err);
      setError(t('customization.fields.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h3 style={{ margin: 0 }}>{t('customization.fields.byModule')}</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={moduleFilter}
            placeholder="Filtrar por módulo (ex: clients)"
            onChange={(event) => setModuleFilter(event.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              minWidth: '220px',
            }}
          />
          <button
            type="button"
            onClick={loadFields}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
            }}
            disabled={loading}
          >{t('support.refreshBtn')}</button>
        </div>
      </div>

      <div
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          backgroundColor: '#fff',
          overflowX: 'auto',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: '#f9fafb' }}>
            <tr>
              {['Módulo', 'Chave', 'Label', 'Tipo', 'Obrigatório', 'Status', 'Ações'].map((header) => (
                <th
                  key={header}
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    fontSize: '13px',
                    color: '#6b7280',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>{t('customization.fields.loading')}</td>
              </tr>
            ) : fields.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
                  Nenhum campo configurado para este filtro.
                </td>
              </tr>
            ) : (
              fields.map((field) => (
                <tr key={`${field.module}-${field.fieldKey}`}>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{field.module}</td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{field.fieldKey}</td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{field.label}</td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>{field.fieldType}</td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                    {field.required ? 'Sim' : 'Não'}
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '999px',
                        backgroundColor: field.isActive ? '#dcfce7' : '#fee2e2',
                        color: field.isActive ? '#166534' : '#b91c1c',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      {field.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #f3f4f6',
                      display: 'flex',
                      gap: '8px',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleEdit(field)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >{t('actions.edit')}</button>
                    <button
                      type="button"
                      onClick={() => handleDelete(field)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #fecaca',
                        backgroundColor: '#fee2e2',
                        color: '#b91c1c',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >{t('actions.remove')}</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          backgroundColor: '#fff',
          padding: '20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
        }}
      >
        <div style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ margin: '0 0 4px 0' }}>Novo campo</h3>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '13px' }}>
            Campos ficam disponíveis para formulários de clientes, propriedades ou serviços.
          </p>
        </div>

        {(message || error) && (
          <div
            style={{
              gridColumn: '1 / -1',
              padding: '10px 12px',
              borderRadius: '6px',
              border: message ? '1px solid #86efac' : '1px solid #fecaca',
              backgroundColor: message ? '#dcfce7' : '#fee2e2',
              color: message ? '#166534' : '#b91c1c',
            }}
          >
            {message || error}
          </div>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          Módulo *
          <input
            type="text"
            value={form.module}
            onChange={(event) => setForm({ ...form, module: event.target.value })}
            placeholder="clients, appointments..."
            required
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          Chave *
          <input
            type="text"
            value={form.fieldKey}
            onChange={(event) => setForm({ ...form, fieldKey: event.target.value })}
            placeholder="ex: dietary_notes"
            required
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          Label *
          <input
            type="text"
            value={form.label}
            onChange={(event) => setForm({ ...form, label: event.target.value })}
            placeholder="Preferências alimentares"
            required
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          Tipo *
          <select
            value={form.fieldType}
            onChange={(event) => setForm({ ...form, fieldType: event.target.value })}
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          >
            {fieldTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
          }}
        >
          <input
            type="checkbox"
            checked={form.required}
            onChange={(event) => setForm({ ...form, required: event.target.checked })}
          />{t('customization.fields.required')}</label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          Posição
          <input
            type="number"
            value={form.position}
            onChange={(event) => setForm({ ...form, position: Number(event.target.value) || 0 })}
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          />
        </label>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
          }}
        >
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
          />
          Campo ativo
        </label>

        <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          Configuração (JSON)
          <textarea
            value={form.configText}
            onChange={(event) => setForm({ ...form, configText: event.target.value })}
            rows={4}
            placeholder='{"options": ["Manhã", "Tarde"]}'
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontFamily: 'monospace',
              fontSize: '13px',
            }}
          />
        </label>

        <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          Validações (JSON)
          <textarea
            value={form.validationsText}
            onChange={(event) => setForm({ ...form, validationsText: event.target.value })}
            rows={4}
            placeholder='{"min_length": 5}'
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontFamily: 'monospace',
              fontSize: '13px',
            }}
          />
        </label>

        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '10px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {saving ? 'Salvando...' : 'Salvar campo'}
          </button>
          <button
            type="button"
            onClick={() =>
              setForm({
                module: '',
                fieldKey: '',
                label: '',
                fieldType: 'text',
                required: false,
                position: 0,
                configText: '',
                validationsText: '',
                isActive: true,
              })
            }
            style={{
              padding: '10px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
            }}
          >{t('common.clear')}</button>
        </div>
      </form>
    </div>
  );
};

export default CompanyCustomFieldsManager;

